import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchProductCategories,
  fetchProductCategory,
  createProductCategory,
  updateProductCategory,
  deleteProductCategory,
} from "../api/productCategories";
import toast from "react-hot-toast";

const initialState = {
  data: [],
  isLoading: false,
  error: false,
  errors: [],
  totalCount: 0,
  currentCount: 0,
};

export const getProductCategoriesAsync = createAsyncThunk(
  "productCategories/getProductCategoriesAsync",
  async (controller, { rejectWithValue }) => {
    try {
      const response = await fetchProductCategories(controller);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getProductCategoryAsync = createAsyncThunk(
  "productCategories/getProductCategoryAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchProductCategory(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProductCategoryAsync = createAsyncThunk(
  "productCategories/createProductCategoryAsync",
  async (data, { rejectWithValue }) => {
    try {
      const response = await createProductCategory(data);
      toast.success("Category created successfully");
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.errors || error.response?.data?.message || "Failed to create category";
      toast.error(errorMessage);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProductCategoryAsync = createAsyncThunk(
  "productCategories/updateProductCategoryAsync",
  async (data, { rejectWithValue }) => {
    try {
      const response = await updateProductCategory(data);
      toast.success("Category updated successfully");
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.errors || error.response?.data?.message || "Failed to update category";
      toast.error(errorMessage);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProductCategoryAsync = createAsyncThunk(
  "productCategories/deleteProductCategoryAsync",
  async (data, { rejectWithValue }) => {
    try {
      const response = await deleteProductCategory(data);
      toast.success("Category deleted successfully");
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.errors || error.response?.data?.message || "Failed to delete category";
      toast.error(errorMessage);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const productCategorySlice = createSlice({
  name: "productCategories",
  initialState,
  reducers: {
    clearProductCategories: (state) => {
      state.data = [];
      state.totalCount = 0;
      state.currentCount = 0;
    },
    updateCategoryInList: (state, action) => {
      const updatedCategory = action.payload;
      const index = state.data.findIndex((cat) => cat.id === updatedCategory.id);
      if (index !== -1) {
        state.data[index] = updatedCategory;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get categories
      .addCase(getProductCategoriesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(getProductCategoriesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.currentCount = action.payload.currentCount || 0;
        state.error = false;
      })
      .addCase(getProductCategoriesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = action.payload;
      })
      // Get single category
      .addCase(getProductCategoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(getProductCategoryAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update or add category to list
        const category = action.payload;
        const index = state.data.findIndex((cat) => cat.id === category.id);
        if (index !== -1) {
          state.data[index] = category;
        } else {
          state.data.push(category);
        }
        state.error = false;
      })
      .addCase(getProductCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = action.payload;
      })
      // Create category
      .addCase(createProductCategoryAsync.pending, (state) => {
        state.isLoading = false; // Don't set loading to true for creates to avoid UI flicker
        state.error = false;
      })
      .addCase(createProductCategoryAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const newCategory = action.payload;
        // Check if category already exists (shouldn't happen, but handle it)
        const exists = state.data.findIndex((cat) => cat.id === newCategory.id) !== -1;
        if (!exists) {
          state.data.push(newCategory);
          state.totalCount += 1;
          state.currentCount += 1;
        }
        state.error = false;
      })
      .addCase(createProductCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = action.payload;
      })
      // Update category
      .addCase(updateProductCategoryAsync.pending, (state) => {
        state.isLoading = false; // Don't set loading to true for updates to avoid UI flicker
        state.error = false;
      })
      .addCase(updateProductCategoryAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedCategory = action.payload;
        
        // Ensure we have a valid category with an id
        if (!updatedCategory || !updatedCategory.id) {
          console.error("Update category response missing id:", updatedCategory);
          state.error = true;
          return;
        }
        
        // Find category by id, handling both string and number types
        const index = state.data.findIndex((cat) => {
          const catId = String(cat.id);
          const updatedId = String(updatedCategory.id);
          return catId === updatedId;
        });
        
        if (index !== -1) {
          // Update the category in place, preserving any additional properties from the existing category
          // and merging in the updated fields
          state.data[index] = {
            ...state.data[index],
            ...updatedCategory,
            // Ensure parent relationship is preserved if it exists in the updated category
            parent: updatedCategory.parent || state.data[index].parent,
            // Ensure children are preserved if not in updated category
            children: updatedCategory.children !== undefined ? updatedCategory.children : state.data[index].children,
          };
        } else {
          // Category not found in list - don't add it to avoid duplicates
          // The list will be refreshed by the component if needed
          console.warn("Updated category not found in list, skipping update:", updatedCategory.id, updatedCategory.title);
        }
        state.error = false;
      })
      .addCase(updateProductCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = action.payload;
      })
      // Delete category
      .addCase(deleteProductCategoryAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(deleteProductCategoryAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = state.data.filter((cat) => cat.id !== action.payload.id);
        state.totalCount -= 1;
        state.currentCount -= 1;
        state.error = false;
      })
      .addCase(deleteProductCategoryAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = action.payload;
      });
  },
});

export const { clearProductCategories, updateCategoryInList } = productCategorySlice.actions;
export default productCategorySlice.reducer;

